import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "@/hooks/platform/useCurrentUser";

export interface MusicTrack {
  id: string;
  title: string;
  artist: string;
  artwork: string | null;
  previewUrl: string | null;
  durationMs: number | null;
  appleUrl: string | null;
  spotifyUrl: string;
  youtubeMusicUrl: string;
  source: "platform-product" | "platform-post" | "itunes" | "spotify";
}

const MUSIC_CATEGORIES = ["music", "audio", "beat", "beats", "track", "song", "sample", "sample-pack"];

function toSearchUrl(kind: "spotify" | "youtube", title: string, artist?: string) {
  const q = encodeURIComponent([title, artist].filter(Boolean).join(" "));
  return kind === "spotify" ? `https://open.spotify.com/search/${q}` : `https://music.youtube.com/search?q=${q}`;
}

export function usePlatformTracks() {
  return useQuery({
    queryKey: ["platform-music-tracks"],
    queryFn: async (): Promise<MusicTrack[]> => {
      const [productsRes, postsRes] = await Promise.all([
        supabase
          .from("products")
          .select("id, title, media_url, category, images, owner_id")
          .eq("is_active", true)
          .order("created_at", { ascending: false })
          .limit(100),
        supabase
          .from("posts")
          .select("id, content, media, post_type, author_id, created_at")
          .in("post_type", ["music", "media", "audio"])
          .order("created_at", { ascending: false })
          .limit(100),
      ]);

      const tracks: MusicTrack[] = [];

      if (!productsRes.error) {
        for (const p of productsRes.data ?? []) {
          const cat = (p.category ?? "").toLowerCase();
          if (!MUSIC_CATEGORIES.some((c) => cat.includes(c))) continue;
          const images = Array.isArray(p.images) ? p.images : [];
          const artwork = typeof images[0] === "string" ? (images[0] as string) : null;
          tracks.push({
            id: `product-${p.id}`,
            title: p.title,
            artist: "Platform upload",
            artwork,
            previewUrl: p.media_url ?? null,
            durationMs: null,
            appleUrl: null,
            spotifyUrl: toSearchUrl("spotify", p.title),
            youtubeMusicUrl: toSearchUrl("youtube", p.title),
            source: "platform-product",
          });
        }
      }

      if (!postsRes.error) {
        for (const post of postsRes.data ?? []) {
          const mediaArr = Array.isArray(post.media) ? post.media : [];
          for (const m of mediaArr as any[]) {
            const url = typeof m === "string" ? m : m?.url;
            const type = typeof m === "object" ? m?.type : undefined;
            if (!url) continue;
            const isAudio = type === "audio" || /\.(mp3|wav|m4a|aac|ogg)$/i.test(url);
            if (!isAudio) continue;
            const title = post.content?.slice(0, 60) || "Untitled track";
            tracks.push({
              id: `post-${post.id}-${url}`,
              title,
              artist: "Platform upload",
              artwork: null,
              previewUrl: url,
              durationMs: null,
              appleUrl: null,
              spotifyUrl: toSearchUrl("spotify", title),
              youtubeMusicUrl: toSearchUrl("youtube", title),
              source: "platform-post",
            });
          }
        }
      }

      return tracks;
    },
  });
}

export function useMusicSearch(query: string, enabled: boolean) {
  return useQuery({
    queryKey: ["music-search", query],
    enabled: enabled && query.trim().length > 1,
    queryFn: async (): Promise<MusicTrack[]> => {
      const { data, error } = await supabase.functions.invoke("music-search", {
        body: { query, limit: 20 },
      });
      if (error) throw error;
      return (data?.tracks ?? []) as MusicTrack[];
    },
  });
}

export function useMusicSources(query: string) {
  const platform = usePlatformTracks();
  const external = useMusicSearch(query || "amapiano", true);

  const platformTracks = useMemo(() => platform.data ?? [], [platform.data]);
  const externalTracks = useMemo(() => external.data ?? [], [external.data]);

  return {
    platformTracks,
    externalTracks,
    isLoading: platform.isLoading || external.isLoading,
    error: platform.error || external.error,
    refetch: () => {
      platform.refetch();
      external.refetch();
    },
  };
}

export interface VenueHistoryEntry {
  id: string;
  venue: string;
  city: string | null;
  date: string;
  status: string;
  origin: "event" | "booking";
}

export function useVenueHistory() {
  const { userId } = useCurrentUser();
  return useQuery({
    queryKey: ["venue-history", userId],
    enabled: !!userId,
    queryFn: async (): Promise<VenueHistoryEntry[]> => {
      const [eventsRes, bookingsRes] = await Promise.all([
        supabase
          .from("events")
          .select("id, title, venue_name, city, event_date, status")
          .eq("owner_id", userId as string)
          .order("event_date", { ascending: false })
          .limit(50),
        supabase
          .from("bookings")
          .select("id, title, event_date, status, venue_id")
          .eq("creator_id", userId as string)
          .order("event_date", { ascending: false })
          .limit(50),
      ]);

      const entries: VenueHistoryEntry[] = [];

      if (!eventsRes.error) {
        for (const e of eventsRes.data ?? []) {
          entries.push({
            id: `event-${e.id}`,
            venue: e.venue_name || e.title,
            city: e.city,
            date: e.event_date,
            status: e.status,
            origin: "event",
          });
        }
      }

      let venueNames = new Map<string, { name: string; city: string | null }>();
      if (!bookingsRes.error && bookingsRes.data?.length) {
        const venueIds = [...new Set(bookingsRes.data.map((b) => b.venue_id))];
        const { data: venues } = await supabase
          .from("profiles")
          .select("id, business_name, username, city")
          .in("id", venueIds);
        venueNames = new Map(
          (venues ?? []).map((v) => [v.id, { name: v.business_name || v.username, city: v.city }]),
        );
      }

      if (!bookingsRes.error) {
        for (const b of bookingsRes.data ?? []) {
          const v = venueNames.get(b.venue_id);
          entries.push({
            id: `booking-${b.id}`,
            venue: v?.name || b.title || "Venue booking",
            city: v?.city ?? null,
            date: b.event_date,
            status: b.status,
            origin: "booking",
          });
        }
      }

      return entries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    },
  });
}
